/**
 * pharmacyLinkLifetime.ts — how long a minted pharmacy sign-in link has left,
 * expressed the way an admin has to say it down a phone.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy sign-in access
 * (INCIDENT 2026-09-11).
 *
 * WHY THIS IS ITS OWN MODULE. The panel that shows the link is a credential
 * surface and its render is deliberately dull; the only thing in it with any
 * logic is the clock, and a clock is worth testing on its own rather than
 * through six renders. `lifetimeAt` is a pure function of (expiry, now), so a
 * spec can walk a link from four hours out to lapsed without a timer.
 *
 * ⚠️ THE UNKNOWN CASE IS NOT THE VALID CASE. orders-service types `expiresAt`
 * as `string | null` — identity has always sent it, but an older build or a
 * stub environment may not, and a bad value would otherwise render as
 * "NaNh NaNm". An expiry we cannot read is reported as `unknown` and the panel
 * says so, because the one thing this must never do is tell an admin a dead
 * credential is good. That is the failure that started the incident.
 */

/**
 * How often the panel re-reads the clock — one second.
 *
 * NOT a display cadence: the visible value only ever moves once a minute, and
 * {@link sameLifetime} lets the ticker hold the previous state object when
 * nothing a reader can see has changed, so React skips those renders. It is how
 * PROMPTLY the panel flips to Expired. Showing "valid" for a credential that
 * died half a minute ago is the exact defect this panel exists to remove, and a
 * second is the cheapest bound on it.
 */
export const COUNTDOWN_TICK_MS = 1_000;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * What we can say about a minted link right now.
 *
 * `live` carries BOTH words the panel prints — the countdown ("3h 16m") and the
 * UK clock time it dies at ("20:30"). They come from one parse of one value on
 * purpose: deriving the clock time separately gives the render a second
 * nullable to defend, and a branch for "live but we could not read the time"
 * that cannot actually happen is a branch nothing can ever test.
 *
 * `expired` is terminal — the link cannot let anybody in and the only move left
 * is a new one. `unknown` means we hold no readable expiry, which is honest but
 * is NOT a promise.
 */
export type PharmacyLinkLifetime =
  | { state: 'live'; label: string; until: string }
  | { state: 'expired' }
  | { state: 'unknown' };

/**
 * `ms` as the coarse, readable duration the CTO asked for — "3h 16m".
 *
 * DELIBERATELY COARSE. Seconds on a credential countdown invite a race that
 * nobody can win, and the number exists to answer one question on a phone call:
 * is the link the pharmacy is holding still worth trying, or do I mint another?
 * Minutes answer that. Days are carried because the link TTL is being extended
 * out of its original hour, and "168h 0m" is not a sentence anyone can read.
 *
 * Anything under a minute says so in words rather than "0m", which reads as
 * already-dead when it is not.
 */
export function formatTimeRemaining(ms: number): string {
  if (ms < MINUTE_MS) return 'less than a minute';
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)}m`;
  if (ms < DAY_MS) return `${Math.floor(ms / HOUR_MS)}h ${Math.floor((ms % HOUR_MS) / MINUTE_MS)}m`;
  return `${Math.floor(ms / DAY_MS)}d ${Math.floor((ms % DAY_MS) / HOUR_MS)}h`;
}

/**
 * What the link looks like at `nowMs`.
 *
 * `<= 0` is expired, not "less than a minute": the boundary belongs on the dead
 * side, so the panel flips the instant the clock passes the expiry rather than
 * showing a reassuring value for one more tick.
 */
export function lifetimeAt(expiresAt: string | null | undefined, nowMs: number): PharmacyLinkLifetime {
  if (!expiresAt) return { state: 'unknown' };
  const expiryMs = Date.parse(expiresAt);
  if (Number.isNaN(expiryMs)) return { state: 'unknown' };
  const remaining = expiryMs - nowMs;
  if (remaining <= 0) return { state: 'expired' };
  return { state: 'live', label: formatTimeRemaining(remaining), until: ukTime(expiryMs) };
}

/**
 * Do these two readings say the same thing to a human?
 *
 * The panel ticks once a second so an expiry lands promptly, but repainting a
 * credential every second for hours is pure waste — this lets the ticker hold
 * the previous state object whenever nothing a reader can see has moved, and
 * React then skips the render entirely.
 */
export function sameLifetime(a: PharmacyLinkLifetime, b: PharmacyLinkLifetime): boolean {
  if (a.state !== b.state) return false;
  return a.state === 'live' && b.state === 'live' ? a.label === b.label && a.until === b.until : true;
}

/**
 * "20:30" on a UK clock — the one the pharmacy reads, and the one an admin says
 * out loud. Module-private and takes an epoch that has ALREADY been validated by
 * {@link lifetimeAt}, so it has no unparseable case to answer for.
 */
function ukTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
}
